import { FC, useMemo } from 'react';
import { TooltipProps } from 'recharts';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { PunchupStackedBarChart } from 'features/common/components/Punchup/primitives/charts/PunchupStackedBarChart';
import { PunchupChartTooltip } from 'features/common/components/Punchup/primitives/charts/PunchupChartTooltip';
import { PunchupHelpButton } from 'features/common/components/Punchup/composites/content/PunchupHelpButton';
import { TimelineControls } from 'features/PunchupPages/Analytics/TimelineControls';
import { TICKETS_SOLD } from 'features/common/hooks/useChart/utils';
import { CHART_SERIES_COLORS } from 'features/common/utils/chartColors';
import { formatCompactNumber } from 'features/common/utils/chartFormatters';
import { customXTick } from 'utils/utils';
import { useChart } from 'features/common/hooks/useChart/useChart';

interface PunchupTicketsSoldSectionProps {
	ticketTypeColors: Record<string, string>;
}

const formatTooltipDate = (label: unknown): string => moment(label as moment.MomentInput).format('MM/DD/YY');

const TicketsIssuedTooltip: FC<TooltipProps<number, string>> = (props) => (
	<PunchupChartTooltip {...props} formatHeader={formatTooltipDate} />
);

const PunchupTicketsSoldSection: FC<PunchupTicketsSoldSectionProps> = ({ ticketTypeColors }) => {
	const { t } = useTranslation();
	const {
		ticketTypeDataFrame,
		dataKeys,
		ticketsSold,
		setDisplay,
		displayWindow,
		isLoading,
		minDate,
		maxDate,
		windowStartDate,
		windowEndDate,
		setDateRange,
	} = useChart(TICKETS_SOLD);

	// Memoised so the bar series keep a stable reference for the React.memo'd PunchupStackedBarChart.
	const bars = useMemo(
		() =>
			(dataKeys ?? []).map((key, i) => ({
				dataKey: key,
				color: ticketTypeColors[key] ?? CHART_SERIES_COLORS[i % CHART_SERIES_COLORS.length],
				name: key,
			})),
		[dataKeys, ticketTypeColors]
	);

	return (
		<PunchupStackedBarChart
			isLoading={isLoading}
			data={(ticketTypeDataFrame ?? []) as unknown as Record<string, unknown>[]}
			bars={bars}
			title={t('analytics-tickets-sold')}
			totalValue={ticketsSold}
			xAxisFormatter={(v) => customXTick(v as Date)}
			yAxisFormatter={formatCompactNumber}
			headerRight={
				<TimelineControls
					setDisplay={setDisplay}
					displayWindow={displayWindow}
					minDate={minDate}
					maxDate={maxDate}
					windowStartDate={windowStartDate}
					windowEndDate={windowEndDate}
					setDateRange={setDateRange}
				/>
			}
			customTooltip={TicketsIssuedTooltip}
			helpButton={
				<PunchupHelpButton
					mainText="Paid tickets sold per ticket type over the selected period — the volume and mix of each tier's sales across the on-sale window. Comped tickets are not shown here; their counts live in Ticket Inventory below."
					secondaryText="If a tier consistently forms the base of every bar, it's driving initial purchases. A tier that only appears in the final week may be selling under urgency rather than sustained demand."
				/>
			}
		/>
	);
};

export default PunchupTicketsSoldSection;
